import React from "react";
// nodejs library to set properties for components
import PropTypes from "prop-types";
// @mui/material components
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Input from "@mui/material/Input";

import {
  primaryColor,
  dangerColor,
  successColor,
  defaultFont
} from "../../styles/material-kit-react.js";
import { useTranslation } from "react-i18next";

const formControlSx = {
  margin: "0 0 17px 0",
  paddingTop: "27px",
  position: "relative",
  "& svg,& .fab,& .far,& .fal,& .fas,& .material-icons": {
    color: "#495057"
  }
};

const labelRootSx = {
  ...defaultFont,
  color: "#AAAAAA !important",
  fontWeight: "400",
  fontSize: "14px",
  lineHeight: "1.42857",
  top: "10px",
  letterSpacing: "unset"
};

const labelRootErrorSx = {
  color: dangerColor + " !important"
};

const labelRootSuccessSx = {
  color: successColor + " !important"
};

// Underline rules live on the Input root (.MuiInput-root). The JSS `$disabled`
// reference becomes the MUI `.Mui-disabled` root class.
const underlineSx = {
  "&:hover:not(.Mui-disabled):before,&:before": {
    borderColor: "#D2D2D2 !important",
    borderWidth: "1px !important"
  },
  "&:after": {
    borderColor: primaryColor
  }
};

const underlineErrorSx = {
  "&:after": {
    borderColor: dangerColor
  }
};

const underlineSuccessSx = {
  "&:after": {
    borderColor: successColor
  }
};

const whiteUnderlineSx = {
  "&:hover:not(.Mui-disabled):before,&:before": {
    borderColor: "#FFFFFF"
  },
  "&:after": {
    borderColor: "#FFFFFF"
  }
};

// JSS `disabled` rule (root &:before) — applies via the .Mui-disabled root slot.
const disabledSx = {
  "&.Mui-disabled:before": {
    borderColor: "transparent !important"
  }
};

// Input slot rules (.MuiInput-input).
const inputSlotSx = {
  "& .MuiInput-input": {
    color: "#495057",
    height: "unset",
    "&,&::placeholder": {
      fontSize: "14px",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: "400",
      lineHeight: "1.42857",
      opacity: "1",
      marginRight: "5px",
      marginLeft: "5px"
    },
    "&::placeholder": {
      color: "#AAAAAA"
    }
  }
};

const whiteInputSlotSx = {
  "& .MuiInput-input": {
    "&,&::placeholder": {
      color: "#FFFFFF",
      opacity: "1"
    }
  }
};

export default function CustomInput(props) {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    formControlProps,
    labelText,
    id,
    labelProps,
    inputProps,
    error,
    white,
    inputRootCustomClasses,
    success,
    onChange,
    value
  } = props;

  const labelSx = {
    ...labelRootSx,
    ...(error ? labelRootErrorSx : {}),
    ...(success && !error ? labelRootSuccessSx : {})
  };

  // Combined sx for the Input root: base underline + state + disabled + input slot.
  const inputSx = {
    ...underlineSx,
    ...(error ? underlineErrorSx : {}),
    ...(success && !error ? underlineSuccessSx : {}),
    ...(white ? whiteUnderlineSx : {}),
    ...disabledSx,
    ...inputSlotSx,
    ...(white ? whiteInputSlotSx : {})
  };

  // Preserve formControlProps.className (external API) while applying base sx.
  const formControlClassName =
    formControlProps !== undefined ? formControlProps.className : undefined;

  return (
    <FormControl
      {...formControlProps}
      className={formControlClassName}
      sx={formControlSx}
    >
      {labelText !== undefined ? (
        <InputLabel
          sx={labelSx}
          htmlFor={id}
          {...labelProps}
          style={
            isRTL === "rtl"
              ? { marginLeft: 210, right: 0, textAlign: "right" }
              : null
          }
        >
          {labelText}
        </InputLabel>
      ) : null}
      <Input
        sx={inputSx}
        className={inputRootCustomClasses}
        id={id}
        value={value || ""}
        onChange={onChange}
        style={{ direction: isRTL === "rtl" ? "rtl" : "ltr", color: "red" }}
        {...inputProps}
      />
    </FormControl>
  );
}

CustomInput.propTypes = {
  labelText: PropTypes.node,
  labelProps: PropTypes.object,
  id: PropTypes.string,
  inputProps: PropTypes.object,
  formControlProps: PropTypes.object,
  inputRootCustomClasses: PropTypes.string,
  error: PropTypes.bool,
  success: PropTypes.bool,
  white: PropTypes.bool
};
